import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowDownRight, ExternalLink, Trash2, Loader2, Pencil, RotateCcw, Lock, Unlock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import TradingViewChart from "./TradingViewChart";
import TVWidget from "./TVWidget";
import ZoneVisualizer, { ZoneBadge, fmtPrice } from "./ZoneVisualizer";

const PRICE_REFRESH_MS = 60_000;

const SECTION = "text-xs font-display font-bold uppercase tracking-[0.18em] text-zinc-400 mb-3 flex items-center gap-2";
const CARD    = "border border-zinc-700/60 bg-zinc-950 rounded-sm";

export default function SetupDetailView({ setupId, onDeleted, onChanged, onEdit, embedded = false }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const refreshTimer = useRef(null);

  const load = async (silent = false) => {
    if (!silent) { setLoading(true); setLoadError(false); }
    try {
      const s = await api.getSetup(setupId);
      setSetup(s);
    } catch {
      if (!silent) setLoadError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (refreshTimer.current) clearInterval(refreshTimer.current);
    refreshTimer.current = setInterval(() => load(true), PRICE_REFRESH_MS);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupId]);

  const handleDelete = async () => {
    if (!window.confirm(`Setup "${setup.asset_name}" wirklich löschen?`)) return;
    await api.deleteSetup(setup.id);
    toast.success("Setup gelöscht.");
    onDeleted?.();
  };

  const handleResetTriggers = async () => {
    await api.resetTriggers(setup.id);
    toast.success("Trigger zurückgesetzt.");
    load(); onChanged?.();
  };

  const handleSetFilledEntry = async () => {
    const val = window.prompt(`Tatsächlicher Entry-Preis für ${setup.asset_name}:`, setup.filled_entry_price || "");
    if (val === null) return;
    const num = parseFloat(val);
    if (isNaN(num) && val !== "") { toast.error("Ungültige Zahl."); return; }
    await api.updateSetup(setup.id, { filled_entry_price: val === "" ? null : num });
    toast.success("Entry-Preis aktualisiert.");
    load(); onChanged?.();
  };

  const handleClose = async () => {
    const val = window.prompt(`Exit-Preis für ${setup.asset_name} (leer = aktueller Preis ${setup.last_price || "?"}):`, setup.last_price || "");
    if (val === null) return;
    const num = val === "" ? setup.last_price : parseFloat(val);
    if (isNaN(num)) { toast.error("Ungültiger Preis."); return; }
    await api.updateSetup(setup.id, { status: "closed", closed_exit_price: num });
    toast.success("Setup geschlossen.");
    load(); onChanged?.();
  };

  const handleReopen = async () => {
    await api.updateSetup(setup.id, { status: "active", closed_exit_price: null });
    toast.success("Setup wieder aktiv.");
    load(); onChanged?.();
  };

  if (loading) return (
    <div className="p-16 flex flex-col items-center justify-center gap-4 text-zinc-500">
      <Loader2 className="animate-spin" size={32} />
      <span className="text-xs font-display uppercase tracking-widest">Lade Setup…</span>
    </div>
  );

  if (loadError) return (
    <div className="border border-rose-900/50 bg-rose-950/20 p-10 flex flex-col items-center gap-4 text-center max-w-md mx-auto mt-8">
      <div className="w-12 h-12 rounded-full border border-rose-800 flex items-center justify-center">
        <span className="text-rose-500 text-xl font-bold">!</span>
      </div>
      <div>
        <div className="font-display font-bold text-sm uppercase tracking-wider text-rose-400 mb-2">Setup konnte nicht geladen werden</div>
        <p className="text-zinc-500 text-sm leading-relaxed">Bitte prüfe die Backend-Verbindung oder versuche es erneut.</p>
      </div>
      <button onClick={() => load()} className="text-sm border border-zinc-700 px-5 py-2 hover:bg-zinc-800 transition-colors font-display uppercase tracking-wider">
        Erneut versuchen
      </button>
    </div>
  );

  if (!setup) return null;

  const isLong = setup.direction === "long";
  const DirIcon = isLong ? ArrowUpRight : ArrowDownRight;
  const dirColor = isLong ? "text-emerald-400" : "text-rose-400";
  const dirBg    = isLong ? "bg-emerald-500/15 border-emerald-500/40" : "bg-rose-500/15 border-rose-500/40";

  const grouped = {
    entry:     setup.zones.filter(z => z.type === "long_entry" || z.type === "short_entry"),
    target:    setup.zones.filter(z => z.type === "target"),
    stop_loss: setup.zones.filter(z => z.type === "stop_loss"),
  };

  const triggeredCount = (setup.triggered_zone_ids || []).length;
  const closed = setup.status === "closed";
  const ch = setup.day_change_percent;
  const chColor = ch == null ? "text-zinc-500" : ch > 0 ? "text-emerald-400" : ch < 0 ? "text-rose-400" : "text-zinc-400";
  const tvSymbol = setup.symbol;

  return (
    <div className="space-y-6" data-testid="setup-detail-view">

      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap pb-4 border-b border-zinc-800">
        {/* Asset name + meta */}
        <div className="space-y-3 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">
              {setup.asset_name}
            </h2>
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 border text-sm font-display font-bold uppercase tracking-widest rounded-sm ${dirBg} ${dirColor}`}>
              <DirIcon size={15} strokeWidth={2.5} />
              {setup.direction}
            </span>
            {closed && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs font-display font-bold uppercase tracking-widest rounded-sm">
                <Lock size={12} /> Closed
              </span>
            )}
          </div>
          {/* Sub-meta */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono-num text-zinc-500 text-sm">{setup.symbol}</span>
            {setup.last_price && (
              <span className="px-3 py-1 bg-white text-black font-mono-num font-bold text-base" data-testid="last-price">
                {fmtPrice(setup.last_price)}
              </span>
            )}
            {ch != null && (
              <span className={`font-mono-num font-bold text-base ${chColor}`}>
                {ch > 0 ? "+" : ""}{ch.toFixed(2)}%
              </span>
            )}
            <span className="text-xs text-zinc-600 uppercase tracking-widest font-display">{setup.category}</span>
            <span className="text-xs text-zinc-600 uppercase tracking-widest font-display">
              {setup.source_type === "friedrich" ? "Friedrich" : setup.source_type === "email" ? "E-Mail" : "Manuell"}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap shrink-0">
          <ActionBtn onClick={handleSetFilledEntry} label="Entry"      icon={DollarSign} testid="btn-set-entry" />
          {triggeredCount > 0 && !closed && (
            <ActionBtn onClick={handleResetTriggers} label="Reset"     icon={RotateCcw}  testid="btn-reset-triggers" />
          )}
          {closed ? (
            <ActionBtn onClick={handleReopen}  label="Öffnen"    icon={Unlock}  testid="btn-reopen"    variant="emerald" />
          ) : (
            <ActionBtn onClick={handleClose}   label="Schließen" icon={Lock}    testid="btn-close"     variant="amber" />
          )}
          <ActionBtn onClick={() => onEdit?.(setup)} label="Bearbeiten" icon={Pencil} testid="btn-edit-detail" />
          {setup.source_url && (
            <a href={setup.source_url} target="_blank" rel="noreferrer">
              <ActionBtn as="div" icon={ExternalLink} label="Quelle" />
            </a>
          )}
          <ActionBtn onClick={handleDelete} label="Löschen" icon={Trash2} testid="btn-delete-detail" variant="rose" />
        </div>
      </div>

      {/* ── ROW 1: 3-column layout ──────────────────────────────────────
           Desktop: [Chart (tall)] [Summary / Entry Zones] [Zone Map (tall)]
           Mobile: stacks vertically                                   */}
      <div className="grid grid-cols-1 lg:grid-cols-[5fr_3fr_3fr] gap-6 lg:items-stretch">

        {/* COL 1: Live Chart — stretches to row height */}
        <div className={`${CARD} flex flex-col`}>
          <SectionHeader label={`Live Chart · ${setup.symbol}`} />
          <div className="p-4 flex-1 min-h-0">
            <TradingViewChart symbol={setup.symbol} height={500} />
          </div>
        </div>

        {/* COL 2: 30% Zusammenfassung / 70% Zonen — both scrollable */}
        <div className="flex flex-col gap-4 min-h-0 h-full">
          {setup.notes && (
            <div className={`${CARD} flex flex-col min-h-0`} style={{ flex: 3 }}>
              <SectionHeader label="Zusammenfassung" />
              <div className="p-4 overflow-y-auto min-h-0 flex-1">
                <p className="text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap">{setup.notes}</p>
              </div>
            </div>
          )}

          {(grouped.entry.length > 0 || grouped.target.length > 0 || grouped.stop_loss.length > 0) && (
            <div className={`${CARD} flex flex-col min-h-0`} style={{ flex: setup.notes ? 7 : 1 }}>
              <SectionHeader label="Zonen" />
              <div className="p-4 overflow-y-auto min-h-0 flex-1 space-y-4">
                <ZoneList title="Entry"     zones={grouped.entry}     triggered={setup.triggered_zone_ids || []} />
                <ZoneList title="Targets"   zones={grouped.target}    triggered={setup.triggered_zone_ids || []} />
                <ZoneList title="Stop Loss" zones={grouped.stop_loss} triggered={setup.triggered_zone_ids || []} />
              </div>
            </div>
          )}
        </div>

        {/* COL 3: Zone Map — stretches to row height */}
        <div className={`${CARD} flex flex-col`}>
          <SectionHeader label="Zone Map" right={<span className="text-[11px] text-zinc-600 font-mono-num">Live · 60s</span>} />
          <div className="p-4 flex-1">
            <ZoneVisualizer zones={setup.zones} currentPrice={setup.last_price} />
          </div>
        </div>
      </div>

      {/* ── ROW 2: Tech Analysis | News + Calendar | Heatmap ───────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        <div className={CARD}>
          <SectionHeader label="Technische Analyse" />
          <div className="p-4">
            <TVWidget
              widgetType="technical-analysis"
              height={350}
              config={{ symbol: tvSymbol, interval: "1D", showIntervalTabs: true, displayMode: "single", width: "100%", height: 350 }}
              testid="tvw-technical"
            />
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className={CARD}>
            <SectionHeader label="News" />
            <div className="p-4">
              <TVWidget
                widgetType="timeline"
                height={190}
                config={{ feedMode: "symbol", symbol: tvSymbol, width: "100%", height: 190, displayMode: "regular" }}
                testid="tvw-news"
              />
            </div>
          </div>
          <div className={CARD}>
            <SectionHeader label="Wirtschaftskalender" />
            <div className="p-4">
              <TVWidget
                widgetType="events"
                height={160}
                config={{ width: "100%", height: 160, importanceFilter: "0,1", countryFilter: "us,eu,de" }}
                testid="tvw-calendar"
              />
            </div>
          </div>
        </div>

        <div className={`${CARD} md:col-span-2 lg:col-span-1`}>
          <SectionHeader label="Stock Heatmap" />
          <div className="p-4">
            <TVWidget
              widgetType="stock-heatmap"
              height={440}
              config={{
                exchanges: [],
                dataSource: "SPX500",
                grouping: "sector",
                blockSize: "market_cap_basic",
                blockColor: "change",
                width: "100%",
                height: 440,
              }}
              testid="tvw-stock-heatmap"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helper components ────────────────────────────────────────────────────────

function SectionHeader({ label, right }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700/60 bg-zinc-900/40">
      <div className="flex items-center gap-2">
        <div className="w-1 h-3.5 bg-zinc-500 rounded-full" />
        <span className="text-xs font-display font-bold uppercase tracking-[0.18em] text-zinc-300">
          {label}
        </span>
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

const VARIANT_CLS = {
  default: "border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white",
  emerald: "border-emerald-800/70 text-emerald-400 hover:bg-emerald-950/50",
  amber:   "border-amber-800/70 text-amber-400 hover:bg-amber-950/50",
  rose:    "border-rose-800/70 text-rose-400 hover:bg-rose-950/50",
};

function ActionBtn({ onClick, icon: Icon, label, testid, variant = "default", as: Tag = "button" }) {
  return (
    <Tag
      onClick={onClick}
      data-testid={testid}
      title={label}
      className={`inline-flex items-center gap-2 h-11 sm:h-9 px-3.5 border bg-transparent text-xs font-display font-bold uppercase tracking-wider transition-colors rounded-sm cursor-pointer ${VARIANT_CLS[variant]}`}
    >
      <Icon size={13} strokeWidth={2} />
      <span className="hidden sm:inline">{label}</span>
    </Tag>
  );
}

function ZoneList({ title, zones, triggered }) {
  if (zones.length === 0) return null;
  return (
    <div className="border border-zinc-700/60 bg-zinc-950 rounded-sm overflow-hidden">
      <div className="px-3 py-2.5 border-b border-zinc-700/60 bg-zinc-900/60">
        <div className="font-display font-bold text-xs uppercase tracking-wider text-zinc-300">{title}</div>
      </div>
      <div className="p-3 space-y-3">
        {zones.map(z => (
          <div key={z.id} className="space-y-1.5">
            <ZoneBadge zone={z} />
            <div className="flex items-center gap-1.5 pl-1">
              {z.label && <span className="text-zinc-500 font-mono-num text-[10px]">{z.label}</span>}
              {triggered.includes(z.id) && (
                <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[9px] font-display font-bold uppercase rounded-sm">HIT</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
